/**
 * Copyright (c) 2026 Spicy Friends Ltd.
 * All rights reserved.
 *
 * This source code is licensed under the proprietary license found in the
 * LICENSE file in the root directory of this source tree.
 */

import { SearchExpand, SideNav, SideNavSection, SideNavTitle } from '@repo/shared-client/components/blocks'
import { SwitchFormField, TagMultiSelectFormField, tagMultiSelectFromTags } from '@repo/shared-client/components/fields'
import { Button } from '@repo/shared-client/components/ui'
import { Form, useFormContext } from '@repo/shared-client/form'
import SFJoinButton from '@web/src/components/primatives/sf-join-button/sf-join-button'
import { useTranslation } from '@web/src/lib/intl/react'
import { FunnelIcon, FunnelPlusIcon, RotateCcwIcon, SearchIcon, TagIcon } from 'lucide-react'
import { useCallback } from 'react'

import type { PublicationListFilterFormValues } from '../publication-list-filter-types'
import { usePublicationListFilterFields, usePublicationListFilterForm } from '../use-publication-list-filter-form'

export interface PublicationListSideNavProps {
  tags: Array<{ id: string; name: string }>
  isAuthenticated: boolean
  hasPermission: (permission: string) => boolean
  filterDefaults: PublicationListFilterFormValues
  filterResetValues: PublicationListFilterFormValues
  onFiltersApply: (values: PublicationListFilterFormValues) => void
  onReset: () => void
}

function PublicationListSideNavFields({
  tags,
  isAuthenticated,
  hasPermission,
  filterResetValues,
  onFiltersApply,
  onReset,
}: Omit<PublicationListSideNavProps, 'filterDefaults'>) {
  const { t } = useTranslation()
  const { reset } = useFormContext<PublicationListFilterFormValues>()
  const { triggerFilterChange, handleSearchChange } = usePublicationListFilterFields(onFiltersApply)

  const handleResetFilters = useCallback(() => {
    reset(filterResetValues)
    onReset()
  }, [filterResetValues, onReset, reset])

  const filtersDisabled = !isAuthenticated

  return (
    <SideNav
      className="h-full min-h-0 flex-1"
      collapseAriaLabel={t('Collapse filters')}
      collapsedTitleIcon={<FunnelIcon />}
      expandAriaLabel={t('Expand filters')}
      header={<SideNavTitle description={t('Refine your search')} title={t('Filters')} />}
      togglePosition="top"
    >
      <SideNavSection className="pt-3" icon={<SearchIcon />} label={t('Search')}>
        <SearchExpand
          clearSearchLabel={t('Clear search')}
          debounceTime={300}
          filterControlsDisabled={filtersDisabled}
          inputProps={{ 'aria-label': t('Search publications') }}
          minChars={0}
          name="search"
          onSearchChange={handleSearchChange}
          placeholder={t('Search by title or author...')}
          toggleFiltersLabel={t('Toggle filters')}
        />
      </SideNavSection>

      <SideNavSection className="pt-3" icon={<TagIcon />} label={t('Tag Filter')}>
        <TagMultiSelectFormField
          disabled={filtersDisabled}
          name="tags"
          onValueChange={() => triggerFilterChange()}
          onValueChangeTrigger="close"
          options={tagMultiSelectFromTags(tags)}
          placeholder={t('All')}
        />
      </SideNavSection>

      <SideNavSection className="pt-3" icon={<FunnelPlusIcon />} label={t('Other Filters')}>
        <div className="flex flex-col gap-3 py-1">
          {isAuthenticated && (
            <SwitchFormField
              disabled={filtersDisabled}
              label={t('Cliterature I Maintain')}
              name="myPublications"
              onCheckedChange={(checked) => triggerFilterChange({ myPublications: checked })}
            />
          )}

          {hasPermission('publication:review') && (
            <SwitchFormField
              disabled={filtersDisabled}
              label={t('Needs Review')}
              name="needsReview"
              onCheckedChange={(checked) => triggerFilterChange({ needsReview: checked })}
            />
          )}
        </div>
      </SideNavSection>

      {isAuthenticated ? (
        <SideNavSection hideWhenCollapsed>
          <Button
            className="mt-4 w-full text-muted-foreground"
            onClick={handleResetFilters}
            size="sm"
            type="button"
            variant="ghost"
          >
            <RotateCcwIcon size={14} />
            {t('Reset Filters')}
          </Button>
        </SideNavSection>
      ) : (
        <SideNavSection hideWhenCollapsed>
          <SFJoinButton className="mt-4 w-full" size="sm" />
        </SideNavSection>
      )}
    </SideNav>
  )
}

export default function PublicationListSideNav({
  tags,
  isAuthenticated,
  hasPermission,
  filterDefaults,
  filterResetValues,
  onFiltersApply,
  onReset,
}: PublicationListSideNavProps) {
  const formMethods = usePublicationListFilterForm(filterDefaults)

  return (
    <Form className="flex h-full min-h-0 w-fit flex-col" formMethods={formMethods} suppressHydrationWarning>
      <PublicationListSideNavFields
        filterResetValues={filterResetValues}
        hasPermission={hasPermission}
        isAuthenticated={isAuthenticated}
        onFiltersApply={onFiltersApply}
        onReset={onReset}
        tags={tags}
      />
    </Form>
  )
}
